import { VideoCropPosition } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import { FacePositions } from '@goldcast/player-config/src/types/speakers-analysis';
import {
  selectSpeakerDetails,
  selectVideoFeeds
} from '@goldcast/player-config/src/selectors/contentMetadataStoreSelectors';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';

export const getDebugVideoCropBoundsForUploads = ({
  facePositions,
  speakerIdToFaceMap,
  broadcastId
}: {
  facePositions: FacePositions[];
  speakerIdToFaceMap: Record<string, number[]>;
  broadcastId: string;
}):
  | {
      videoCropPositionMap: Record<string, VideoCropPosition>;
      faceBoundingBoxMap: Record<string, VideoCropPosition>;
      personBoundingBoxMap: Record<string, VideoCropPosition>;
      personContainerBoundingBoxMap: Record<string, VideoCropPosition>;
    }
  | undefined => {
  const storeState = useContentMetadataStore.getState();
  const speakerDetails = selectSpeakerDetails(broadcastId)(storeState);
  const videoFeeds = selectVideoFeeds(broadcastId)(storeState);

  const speakerKeyToSpeakerIdMap = Object.keys(speakerDetails).reduce(
    (acc, speakerKey) => {
      acc[speakerKey] = speakerDetails[speakerKey].id;
      return acc;
    },
    {} as Record<string, string>
  );

  const videoIdToSpeakerKeyMap = videoFeeds.reduce(
    (acc, feed) => {
      acc[feed.id] = feed.speakerKey;
      return acc;
    },
    {} as Record<string, string>
  );

  const videoCropPositionMap: Record<string, VideoCropPosition> = {};
  const videoPersonBoundingBoxMap: Record<string, VideoCropPosition> = {};
  const videoFaceBoundingBoxMap: Record<string, VideoCropPosition> = {};
  const videoPersonContainerBoundingBoxMap: Record<string, VideoCropPosition> = {};

  Object.keys(videoIdToSpeakerKeyMap).forEach(videoId => {
    const speakerKey = videoIdToSpeakerKeyMap[videoId];
    if (!speakerKey) {
      return;
    }

    const speakerId = speakerKeyToSpeakerIdMap[speakerKey];
    if (!speakerId) {
      return;
    }

    const faceIds = speakerIdToFaceMap[speakerId] || [];
    const facePosition = facePositions.find(fp => faceIds.includes(fp.face_id));

    if (!facePosition) {
      return;
    }

    const faceBoundingBox = facePosition.crop_position_face || facePosition.crop_position;
    const personBoundingBox = facePosition.crop_position_person;
    const personContainerBoundingBox = facePosition.crop_position_person_container;

    if (!faceBoundingBox) {
      return;
    }

    const boundingBoxToUse = personBoundingBox || faceBoundingBox;

    videoFaceBoundingBoxMap[videoId] = faceBoundingBox;
    videoPersonBoundingBoxMap[videoId] = personBoundingBox || faceBoundingBox;
    if (personContainerBoundingBox) {
      videoPersonContainerBoundingBoxMap[videoId] = personContainerBoundingBox;
    }
    videoCropPositionMap[videoId] = boundingBoxToUse;
  });

  return Object.keys(videoCropPositionMap).length > 0
    ? {
        videoCropPositionMap,
        faceBoundingBoxMap: videoFaceBoundingBoxMap,
        personBoundingBoxMap: videoPersonBoundingBoxMap,
        personContainerBoundingBoxMap: videoPersonContainerBoundingBoxMap
      }
    : undefined;
};
